<script context="module">export const META = {
  height: 200,
  topMargin: 18,
  bottomMargin: 0,
  leftMargin: 100,
  // ...when font-size set to 12px and 7 character ticks
  rightMargin: 250
};
</script>

<script>import * as d3 from "d3";
import { v4 as uuid } from "uuid";
import { okhsl } from "../../../d3-mapp/color/index.js";
import { getContext } from "svelte";
import ScalarAxis from "../ScalarAxis.svelte";
import GraphPlot from "../GraphPlot.svelte";
import SignalProjection, {
} from "./SignalProjection.svelte";
import SignalHistogram from "./SignalHistogram.svelte";
import { getSplineDomain } from "../../../dsp/spline.js";
import {
  timeRangeContextKey
} from "../time-plot-context.js";
export let title = void 0;
export let label;
export let labelSwatch = void 0;
export let tags = void 0;
export let scalarEther = true;
export let showAxis = true;
export let showThreshold = true;
export let showProjection = true;
export let showHistogram = true;
export let projectionHeight = 10;
export let titleHeight = 12;
export let height = META.height;
export let paths = [];
export let projectionData;
export let histogramData;
export let graphStrokes;
export let projectionStrokes = void 0;
const { timeScale, tickTextMetrics, plotConfigMap, setPlot } = getContext(timeRangeContextKey);
const id = uuid();
$:
  params = {
    id,
    height,
    margin: [
      META.topMargin,
      META.rightMargin,
      META.bottomMargin,
      META.leftMargin
    ],
    axis: showAxis,
    histogram: showHistogram,
    thresholds: showThreshold
  };
$: {
  setPlot(params);
}
$:
  config = $plotConfigMap.get(id);
let yScale;
const topMargin = META.topMargin;
$:
  plotTitleHeight = title && title.length > 0 ? titleHeight : 0;
$:
  tagProjectionHeight = showProjection ? projectionHeight : 0;
$:
  plotHeight = height - topMargin - tagProjectionHeight - plotTitleHeight;
$:
  histogramHeight = height - topMargin;
$:
  histogramWidth = config ? config.rightIndent : META.rightMargin;
let ticks = void 0;
$: {
  if ($timeScale) {
    let yExtent = paths.map((path) => getSplineDomain(path)).reduce(
      (extent, domain) => {
        if (extent && domain && domain[0] != void 0 && domain[1] != void 0) {
          return [
            Math.min(extent[0], domain[0].valueOf()),
            Math.max(extent[1], domain[1].valueOf())
          ];
        } else {
          return domain;
        }
      },
      void 0
    );
    const yRange = [plotHeight + topMargin + plotTitleHeight, topMargin + plotTitleHeight];
    yScale = yExtent && yExtent[0] != void 0 && yExtent[1] != void 0 ? d3.scaleLinear(yExtent, yRange).nice() : void 0;
    const scalarTickHeight = tickTextMetrics.actualBoundingBoxAscent + tickTextMetrics.actualBoundingBoxDescent;
    const maxTicks = Math.floor(plotHeight / scalarTickHeight);
    ticks = ticks !== void 0 ? Math.min(ticks, maxTicks) : maxTicks;
  }
}
</script>

{#if $timeScale}
  <g class="seriesPlot plot" transform={config?.transform} data-plot={id} style="--title-font-size:{plotTitleHeight}px;">
    {#if title && title.length > 0}
      <title>{title}</title>
      <g class="title" transform="translate({(config ? config.leftIndent : META.leftMargin) + ($timeScale.range()[1] - $timeScale.range()[0])/2} ,{plotTitleHeight+topMargin})">
      <text
      fill="currentColor"

        
        text-anchor="middle">{title}</text
      >
    </g>
    {/if}
    {#if yScale}
    <g
      class={`left ${config?.axis ? "" : "hidden"}`}
      transform="translate({config ? config.leftIndent : META.leftMargin},0)"
      ><ScalarAxis
        scale={yScale}
        {label}
        {labelSwatch}
        ether={scalarEther
          ? $timeScale.range()[1] - $timeScale.range()[0]
          : $timeScale.range()[0] - $timeScale.range()[1]}
        thresholds={params.thresholds ? tags : []}
        {ticks}
      /></g
    >
    {#if paths.length > 0}
      <g>
        <GraphPlot
          {paths}
          xScale={$timeScale}
          {yScale}
          strokes={graphStrokes}
          defaultStroke={{ color: okhsl("black"), width: 1 }}
        />
      </g>
    {/if}
    {/if}
    {#if showProjection}
      <g
        class={`projection ${showProjection ? "" : "hidden"}`}
        transform="translate(0,{topMargin + plotHeight + plotTitleHeight})"
        ><SignalProjection
          data={projectionData}
          strokes={projectionStrokes ?? graphStrokes}
          xScale={$timeScale}
          defaultFill={okhsl("hsl(0,25%,30%,0.5)")}
          height={projectionHeight}
          bind:size={tagProjectionHeight}
        /></g
      >{/if}
    {#if config?.histogram}
      <g
        class={`right ${config?.histogram ? "" : "hidden"}`}
        transform="translate({$timeScale.range()[1] +
          topMargin / 2},{topMargin +
          (height - topMargin - histogramHeight) / 2})"
        ><SignalHistogram
          type="vbar"
          padding={20}
          data={histogramData}
          defaultFill={okhsl("hsl(0,25%,30%,0.5)")}
          width={histogramWidth}
          height={histogramHeight}
        /></g
      >
    {/if}
  </g>
{/if}

<style>
  g.seriesPlot {
    --title-font-size: 12px;
  }
  g.seriesPlot g.left.hidden :global(g.axis text),
  g.seriesPlot g.left.hidden :global(g.axis g.sub-axis g.tick line:not(.ether)),
  g.seriesPlot g.left.hidden :global(g.axis g.sub-axis g.tick text),
  g.seriesPlot g.left.hidden :global(g.axis g.sub-axis path) {
    display: none;
  }

  g.seriesPlot g.right.hidden {
    display: none;
  }

  g.seriesPlot g.projection.hidden {
    display: none;
  }

  g.title text {
    font-size: var(--title-font-size, 12px);
    fill-opacity: 1;
    font-family: Roboto, sans-serif;
  }
</style>
